/**
 * Service Form Page (Create/Edit)
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Avatar,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Image as ImageIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
  useGetServiceQuery,
  useCreateServiceMutation,
  useUpdateServiceMutation,
} from '../../store/api/servicesApi';
import { ServiceFormData } from '../../types/service';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const ServiceFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { data: serviceData, isLoading: isLoadingService } = useGetServiceQuery(Number(id!), {
    skip: !isEdit,
  });

  const [createService, { isLoading: isCreating }] = useCreateServiceMutation();
  const [updateService, { isLoading: isUpdating }] = useUpdateServiceMutation();

  const [formData, setFormData] = useState<ServiceFormData>({
    name: '',
    name_ar: '',
    slug: '',
    description: '',
    description_ar: '',
    is_active: true,
    uses_dual_pricing: false,
    order: 1,
  });
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);

  // Load service data when editing
  useEffect(() => {
    if (isEdit && serviceData?.data) {
      const service = serviceData.data;
      setFormData({
        name: service.name,
        name_ar: service.name_ar || '',
        slug: service.slug,
        description: service.description,
        description_ar: service.description_ar || '',
        is_active: service.is_active,
        uses_dual_pricing: service.uses_dual_pricing ?? false,
        order: service.order,
      });
      setIconPreview(service.icon || null);
    }
  }, [isEdit, serviceData]);

  const handleInputChange = (field: keyof ServiceFormData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('name_ar', formData.name_ar);
      formDataToSend.append('slug', formData.slug);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('description_ar', formData.description_ar);
      formDataToSend.append('is_active', formData.is_active.toString());
      formDataToSend.append('uses_dual_pricing', formData.uses_dual_pricing.toString());
      formDataToSend.append('order', formData.order.toString());
      if (iconFile) {
        formDataToSend.append('icon', iconFile);
      }

      if (isEdit) {
        await updateService({ id: Number(id!), data: formDataToSend }).unwrap();
        toast.success(t('services.toast.updated'));
      } else {
        await createService(formDataToSend).unwrap();
        toast.success(t('services.toast.created'));
      }
      navigate('/services');
    } catch (err: any) {
      toast.error(
        err?.data?.message ||
          (isEdit ? t('services.toast.updateFailed') : t('services.toast.createFailed'))
      );
    }
  };

  if (isEdit && isLoadingService) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={isEdit ? t('services.form.editTitle') : t('services.form.createTitle')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.services'), path: '/services' },
          { label: isEdit ? t('services.breadcrumbs.edit') : t('services.breadcrumbs.create') },
        ]}
      />

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/services')}
        sx={{ mb: 3 }}
      >
        {t('services.buttons.backToServices')}
      </Button>

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Icon Preview/Upload */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('services.serviceIcon')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                  <Avatar
                    src={iconPreview || undefined}
                    variant="rounded"
                    sx={{ width: 120, height: 120 }}
                  >
                    <ImageIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Button variant="outlined" component="label" size="medium">
                    {iconPreview ? t('services.buttons.changeIcon') : t('services.buttons.uploadIcon')}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleIconChange}
                    />
                  </Button>
                  {iconPreview && (
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      onClick={() => {
                        setIconFile(null);
                        setIconPreview(isEdit && serviceData?.data?.icon ? serviceData.data.icon : null);
                      }}
                    >
                      {t('services.buttons.remove')}
                    </Button>
                  )}
                </Box>
                <ImageSizeHint size={IMAGE_SIZES.serviceIcon} />
              </Box>

              <TextField
                fullWidth
                label={t('services.fields.name')}
                value={formData.name}
                onChange={(e) => {
                  handleInputChange('name', e.target.value);
                  // Auto-generate slug from name (only when creating)
                  if (!isEdit) {
                    handleInputChange(
                      'slug',
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/(^-|-$)/g, '')
                    );
                  }
                }}
                required
                disabled={isCreating || isUpdating}
              />

              <TextField
                fullWidth
                label={t('services.fields.nameAr')}
                value={formData.name_ar}
                onChange={(e) => handleInputChange('name_ar', e.target.value)}
                disabled={isCreating || isUpdating}
                inputProps={{ dir: 'rtl' }}
              />

              <TextField
                fullWidth
                label={t('services.fields.slug')}
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                required
                helperText={t('services.form.slugHelper')}
                disabled={isCreating || isUpdating}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('services.fields.description')}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                required
                disabled={isCreating || isUpdating}
              />

              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('services.fields.descriptionAr')}
                value={formData.description_ar}
                onChange={(e) => handleInputChange('description_ar', e.target.value)}
                disabled={isCreating || isUpdating}
                inputProps={{ dir: 'rtl' }}
              />

              <TextField
                fullWidth
                type="number"
                label={t('services.fields.order')}
                value={formData.order}
                onChange={(e) => handleInputChange('order', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
                disabled={isCreating || isUpdating}
                InputProps={{
                  startAdornment: <InputAdornment position="start">#</InputAdornment>,
                }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.uses_dual_pricing}
                    onChange={(e) => handleInputChange('uses_dual_pricing', e.target.checked)}
                    disabled={isCreating || isUpdating}
                  />
                }
                label={t('services.form.dualPricingLabel')}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                {t('services.form.dualPricingHint')}
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    disabled={isCreating || isUpdating}
                  />
                }
                label={t('services.form.active')}
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/services')}
                  disabled={isCreating || isUpdating}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={
                    isCreating ||
                    isUpdating ||
                    !formData.name ||
                    !formData.slug ||
                    !formData.description
                  }
                >
                  {isCreating || isUpdating
                    ? t('services.buttons.saving')
                    : isEdit
                    ? t('services.buttons.updateService')
                    : t('services.buttons.createService')}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ServiceFormPage;

